import { BookOpenTextIcon, CalendarCheckIcon, CalendarXIcon, ClipboardCheckIcon, FileTextIcon, FolderSearchIcon, LayersIcon, MessageCircleIcon, MicOffIcon, TimerIcon, UsersIcon } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

// Mockup standard «ENGELSK KLAR™ · 6 bind» (Recursos Impultienda, tienda ENGELSK KLAR). Ver engelsk-klar/media/MOCKUP.md
export const PRODUCT_IMAGE = 'https://aekpgsbqjzqntlbucbts.supabase.co/storage/v1/object/public/store-resources/79dccd1c-bd55-4996-abbb-ee0038764a5e/1792695295534_engelskklar-mockup-caja-6-bind-415ad0c3.webp';
export const BRAND = 'ENGELSK KLAR™';
export const DISCLAIMER = 'Ikke en offisiell utgivelse fra Utdanningsdirektoratet. Materiellet er tilpasset kompetansemålene i LK20 – les alltid læreplanen på udir.no.';

export const heroGuarantees = ['Direkte nedlasting', 'Engangsbetaling', 'Ingen abonnement', '7 dager pengene-tilbake-garanti'];

export const stats = [{
  value: '6',
  label: 'Digitale bind'
}, {
  value: '240',
  label: 'Sider klare til bruk'
}, {
  value: '5',
  label: 'Bonuser inkludert'
}, {
  value: 'PDF',
  label: 'Klar til utskrift'
}, {
  value: '1–10',
  label: 'Trinn'
}];

export const differenceStats = [{
  value: '1.–10. trinn',
  label: 'Fra de første ordene på småtrinnet til argumentasjon på ungdomstrinnet'
}, {
  value: '6 digitale bind',
  label: 'Fra årsplan og ferdige timer til speaking, lesing, grammatikk, kultur og vurdering'
}, {
  value: 'PDF klar til utskrift',
  label: 'Skriv ut elevarkene eller vis dem på tavla'
}];

export const beforeItems: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Søndagskvelden går til planlegging',
  text: 'Du sitter foran PC-en søndag kveld og planlegger ukas engelsktimer – og blir likevel ikke ferdig.'
}, {
  title: 'Store forskjeller i samme klasse',
  text: 'Noen snakker nesten flytende etter YouTube og spill, andre tør knapt si «hello». Timen må fungere for alle.'
}, {
  title: 'Elevene tør ikke snakke',
  text: 'Du vil ha mer muntlig engelsk, men det blir stille i klasserommet når elevene skal snakke.'
}, {
  title: 'Engelsk uten egen fagbakgrunn',
  text: 'Mange kontaktlærere underviser engelsk uten å ha det som fag – og må lage alt selv.'
}, {
  title: 'Kompetansemål og vurdering',
  text: 'Hva skal elevene kunne etter 4., 7. og 10. trinn – og hvordan vurderer du det underveis?'
}, {
  title: 'Leting etter opplegg på nettet',
  text: 'Arkene du finner passer sjelden trinnet, temaet eller nivåene i akkurat din klasse.'
}];

export const afterItems = ['Et ferdig årshjul for 1.–4., 5.–7. og 8.–10. trinn', '12 ferdige engelsktimer minutt for minutt', 'Elevark i 3 nivåer – klare til utskrift', 'Key phrases på engelsk og lærerinstruksjon på norsk', '50 speaking-aktiviteter som får elevene til å snakke', 'Lesetekster og skriverammer i tre nivåer', 'Grammatikk forklart på norsk, øvelser på engelsk', 'Kultur og tekster fra den engelskspråklige verden', 'Rubrikker, prøver med fasit og 100 ferdige tilbakemeldinger'];

export const recognizeItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: CalendarXIcon,
  title: 'Søndagskvelden går med til planlegging',
  text: 'Bruker du søndagskvelden på å planlegge ukas engelsktimer fra bunnen av?'
}, {
  icon: UsersIcon,
  title: 'Klassen har helt ulike nivåer',
  text: 'Trenger du timer der eleven som sliter og eleven som snakker flytende kan jobbe med det samme – på hvert sitt nivå?'
}, {
  icon: MicOffIcon,
  title: 'Det blir stille når elevene skal snakke',
  text: 'Skulle du ønske at flere elever turte å snakke engelsk i timen?'
}, {
  icon: ClipboardCheckIcon,
  title: 'Vurdering tar for mye tid',
  text: 'Skulle du ønske at rubrikker, prøver med fasit og tilbakemeldinger var ferdig skrevet?'
}, {
  icon: TimerIcon,
  title: 'Klokka er 08.25 og ingenting er klart',
  text: 'Står du ved kopimaskinen rett før timen – uten et elevark som passer klassen?'
}, {
  icon: FolderSearchIcon,
  title: 'Du leter etter opplegg på nettet',
  text: 'Bruker du tid på å lete etter ark og aktiviteter som passer trinnet og temaet?'
}];

// Spine colours mirror the box: navy, sky blue, green, lime, orange, red.
export const volumes: Array<{
  number: string;
  level: string;
  title: string;
  tagline: string;
  intro: string;
  items: string[];
  outro: string;
  note?: string;
  spine: string;
  rule: string;
}> = [{
  number: 'Bind 01',
  level: 'Årsplan',
  title: 'Årsplan og 12 ferdige engelsktimer',
  tagline: 'Vit nøyaktig hva hver engelsktime skal inneholde – hele skoleåret.',
  intro: 'Det første bindet gir deg årshjulet for hvert trinn og tolv ferdige engelsktimer du kan bruke direkte.',
  items: ['Årshjul for 1.–4., 5.–7. og 8.–10. trinn', 'Kjerneelementene i praksis', 'Ukeplan og timestruktur', 'Classroom language-plakat', '12 timer minutt for minutt', 'Elevark i 3 nivåer til hver time', 'Fasit til elevarkene', '12-ukers timekalender og exit tickets'],
  outro: 'Ferdige timer som gjør at du møter klassen med en tydelig plan.',
  spine: 'bg-blue-900 text-white',
  rule: 'border-white/30'
}, {
  number: 'Bind 02',
  level: 'Speaking',
  title: 'Speaking: muntlig engelsk som alle tør',
  tagline: 'Femti aktiviteter som får hele klassen til å snakke.',
  intro: 'Det andre bindet samler speaking-aktiviteter for 1.–4., 5.–7. og 8.–10. trinn, hver med tid, organisering, fraser på engelsk og tre nivåer.',
  items: ['50 speaking-aktiviteter', 'Pararbeid og info gap', 'Rollespill og samtalekort', '«Talk for 1 minute»', 'Debatter for ungdomstrinnet', 'Sentence starters', 'Trygg klassekultur', 'Muntlig vurdering'],
  outro: 'Muntlig engelsk hver uke – uten at du må finne på alt selv.',
  spine: 'bg-sky-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Bind 03',
  level: '3 nivåer',
  title: 'Lesing og skriving i tre nivåer',
  tagline: 'Samme tekst i tre nivåer – hele klassen snakker om det samme etterpå.',
  intro: 'Det tredje bindet gir deg lesetekster på engelsk i nivå A, B og C, med spørsmål, fasit og skriverammer.',
  items: ['12 lesetekster i tre nivåer', 'Spørsmål med fasit', 'Lesestrategier', 'Skriverammer: postcard, e-mail, story', 'Argumenterende tekst', 'Modelltekster'],
  outro: 'Tilpasset opplæring uten å lage tre versjoner selv.',
  spine: 'bg-green-600 text-white',
  rule: 'border-white/30'
}, {
  number: 'Bind 04',
  level: 'Ordforråd',
  title: 'Grammatikk og ordforråd uten kjedsomhet',
  tagline: 'Forklart på norsk. Øvd på engelsk. Med fasit.',
  intro: 'Det fjerde bindet gir deg ordlister etter tema og trinn og grammatikk forklart på norsk med øvelser og spill på engelsk.',
  items: ['Ordlister etter tema og trinn', 'Present simple og continuous', 'Past tense og irregular verbs', 'Question words', 'Prepositions og adjectives', 'Spill for repetisjon', 'Fasit til alle øvelser'],
  outro: 'Grammatikk som elevene faktisk husker.',
  spine: 'bg-lime-400 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Bind 05',
  level: 'Kultur',
  title: 'Den engelskspråklige verden',
  tagline: 'Land, kultur, urfolk og høytider – med tekster og oppgaver.',
  intro: 'Det femte bindet tar klassen med til den engelskspråklige verden, med faktasider og tekster i tre nivåer.',
  items: ['Storbritannia, USA og Irland', 'Australia, Canada, India og Sør-Afrika', 'Urfolk', 'Engelsk som verdensspråk', 'Halloween, Thanksgiving, Guy Fawkes og Christmas', 'Prosjekter med tverrfaglige temaer'],
  outro: 'Møte med engelskspråklige tekster – klart til timen.',
  spine: 'bg-orange-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Bind 06',
  level: 'Vurdering',
  title: 'Vurdering og kompetansemål',
  tagline: 'Vit hva elevene skal kunne – og hvordan du vurderer det.',
  intro: 'Det sjette bindet gir deg målene i elevvennlig språk, rubrikker, prøver og ferdige tilbakemeldinger.',
  items: ['Mål etter 2., 4., 7. og 10. trinn i elevvennlig språk', '«I can»-setninger', 'Rubrikker for muntlig og skriftlig', 'Prøver A/B med fasit', 'Egenvurdering', '100 ferdige tilbakemeldinger'],
  outro: 'Underveisvurdering uten kveldsarbeid.',
  note: `${BRAND} er et selvstendig utviklet materiell. ${DISCLAIMER}`,
  spine: 'bg-red-600 text-white',
  rule: 'border-white/30'
}];

export const volumesIntro = {
  title: '6 digitale bind for engelsklærere',
  text: 'Et komplett system som hjelper deg å planlegge og gjennomføre engelskundervisningen gjennom hele skoleåret. Hvert bind har 40 sider med ferdige timer, elevark og oppgaver klare til utskrift.'
};

export const packItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: BookOpenTextIcon,
  title: 'Ferdige timer minutt for minutt',
  text: 'Oppstart · hoveddel · avslutning. Med materiell, lærerens instruksjon på norsk og key phrases på engelsk.'
}, {
  icon: FileTextIcon,
  title: 'Elevark klare til utskrift',
  text: 'Elevark til hver time som du skriver ut eller viser på tavla – ingen leting i mapper.'
}, {
  icon: LayersIcon,
  title: 'Tre nivåer i hver time',
  text: 'Samme tema, tre nivåer. Elever som sliter og elever som snakker flytende jobber sammen.'
}, {
  icon: MessageCircleIcon,
  title: 'Speaking som alle tør',
  text: 'Pararbeid, rollespill, samtalekort og sentence starters som gjør det trygt å snakke engelsk.'
}, {
  icon: ClipboardCheckIcon,
  title: 'Prøver, rubrikker og fasit',
  text: 'Prøver A/B med fasit, rubrikker for muntlig og skriftlig og 100 ferdige tilbakemeldinger.'
}, {
  icon: CalendarCheckIcon,
  title: 'Årshjul for hvert trinn',
  text: 'Temaer måned for måned for 1.–4., 5.–7. og 8.–10. trinn – koblet til kjerneelementene i LK20.'
}];

export { pageSamples } from './pageSamples';
export const pageTypes = ['Årshjul', 'Ferdige timer', 'Elevark i 3 nivåer', 'Key phrases og fasit'];

export const bonuses: Array<{
  title: string;
  text: string;
  items: string[];
  benefit: string;
}> = [{
  title: 'Vikarpakken',
  text: 'Ferdige engelsktimer vikaren kan gjennomføre når du er borte.',
  items: ['12 ferdige timer', 'Ingen forberedelse', 'Tydelige instrukser', 'Klart til utskrift'],
  benefit: 'Syk eller på kurs? Engelsktimen går likevel.'
}, {
  title: 'Engelsk på 5 minutter',
  text: 'Printbare warm-up-kort som får klassen i gang med engelsk på et øyeblikk.',
  items: ['60 warm-up-kort', 'Alle trinn', 'Muntlige oppstarter', 'Klart til utskrift'],
  benefit: 'En ny oppstart hver time – uten forberedelse.'
}, {
  title: 'Klasseromsplakater',
  text: 'Fargerike plakater til veggen i klasserommet.',
  items: ['20 plakater', 'Classroom language', 'Question words og tenses', 'Days, months og numbers'],
  benefit: 'Engelsk på veggen – hjelp for elevene hver dag.'
}, {
  title: 'Engelskbingo og klasseromsspill',
  text: 'Ferdige spill som gjør repetisjon morsomt.',
  items: ['Bingobrett etter tema', 'Who am I?', 'Taboo-kort', 'Simon says'],
  benefit: 'Ti minutter med spill – og ordene sitter.'
}, {
  title: 'Foreldrekommunikasjon',
  text: 'Ferdige meldinger til foresatte på norsk.',
  items: ['40 ferdige meldinger', 'Skolestart og nytt tema', 'Lekser og prøver', 'Tips til engelsk hjemme'],
  benefit: 'Meldingen hjem skrevet på et minutt.'
}];

export const steps: Array<{
  title: string;
  text: string;
}> = [{
  title: `Kjøp ${BRAND}`,
  text: 'Gjennomfør kjøpet på et par minutter. Engangsbetaling, ingen abonnement.'
}, {
  title: 'Last ned PDF-ene på e-post',
  text: 'Du får PDF-ene tilsendt på e-post med en gang etter kjøpet.'
}, {
  title: 'Velg timen og møt klassen',
  text: 'Finn uka i årshjulet, skriv ut elevarket i tre nivåer – og møt klassen med en tydelig plan.'
}];

export const audienceItems = ['Underviser engelsk på 1.–10. trinn', 'Er kontaktlærer og har engelsk uten å ha det som fag', 'Har elever på helt ulike nivåer i samme klasse', 'Vil få flere elever til å snakke engelsk', 'Vil slippe å planlegge hver time fra bunnen av', 'Vil ha elevark, prøver og fasit klare', 'Vil ha vurdering koblet til kompetansemålene', 'Vil ha søndagskvelden tilbake'];

export const timeValueTasks = ['Lete etter opplegg på nettet', 'Planlegge ukas engelsktimer', 'Lage elevark i flere nivåer', 'Lage prøver og fasit', 'Skrive tilbakemeldinger'];

export const offerRows: Array<{
  label: string;
  detail: string;
}> = [{
  label: '6 digitale bind – 240 sider',
  detail: 'Årsplan og ferdige timer, speaking, lesing og skriving, grammatikk og ordforråd, den engelskspråklige verden og vurdering'
}, {
  label: '5 bonuser uten ekstra kostnad',
  detail: 'Vikarpakken, warm-up-kort, klasseromsplakater, engelskbingo og spill og meldinger til foresatte'
}, {
  label: 'For engelsklærere 1.–10. trinn',
  detail: 'Ferdige timer, elevark i 3 nivåer, key phrases, rubrikker og prøver med fasit'
}];

export const offerIncludes = ['Direkte nedlasting', 'PDF-filer på e-post', 'Klar til utskrift', 'Ingen fysisk vare sendes'];
export const PRICE = '299 kr';
export const PRICE_BEFORE = '599 kr';

// No testimonials on this page. Only authentic reviews may ever be added here.
export const testimonials: Array<{
  quote: string;
  name: string;
  location: string;
}> = [];

export const faqs = [{
  q: 'Hvilket format har materiellet?',
  a: `${BRAND} leveres digitalt som PDF-filer. Du kan bruke dem på PC, nettbrett eller mobil og skrive ut sidene du trenger.`
}, {
  q: 'Hvordan får jeg materiellet?',
  a: 'Du får PDF-ene på e-post med en gang etter kjøpet. Ingen ventetid og ingen fysisk vare.'
}, {
  q: 'Hvilke trinn passer det for?',
  a: 'For 1.–10. trinn. Årshjulene og timene er delt i 1.–4., 5.–7. og 8.–10. trinn, og elevarkene har tre nivåer.'
}, {
  q: 'Hvilket språk er materiellet på?',
  a: 'Lærerinstruksjonene står på norsk bokmål. Alt elevmateriell – elevark, key phrases, tekster og oppgaver – står på engelsk.'
}, {
  q: 'Passer det for klasser med store forskjeller?',
  a: 'Ja. Elevarkene og lesetekstene finnes i tre nivåer om samme tema, så hele klassen kan jobbe med det samme og snakke sammen etterpå.'
}, {
  q: 'Kan jeg skrive det ut?',
  a: 'Ja. Alt er laget klart til utskrift. Du velger selv hva du skriver ut, og du kan også vise sidene på tavla.'
}, {
  q: 'Trenger jeg engelsk som fag for å bruke det?',
  a: 'Nei. Timene er beskrevet minutt for minutt med lærerens instruksjon på norsk og ferdige key phrases på engelsk. Materiellet passer både for faglærere og for kontaktlærere som har engelsk.'
}, {
  q: 'Er det godkjent av Udir?',
  a: `Nei. ${BRAND} er et selvstendig utviklet materiell. ${DISCLAIMER}`
}, {
  q: 'Kan jeg virkelig være klar til timen på 5 minutter?',
  a: `${BRAND} er laget for å gjøre forberedelsene raskere. Du finner uka i årshjulet, leser gjennom timen og skriver ut elevarket – og er klar på omtrent fem minutter. Tiden kan variere etter timen, klassen og behovet for tilpasning.`
}, {
  q: 'Er de fem bonusene inkludert?',
  a: `Ja. Alle fem bonusene er inkludert uten ekstra kostnad – alt for ${PRICE}.`
}, {
  q: 'Er det et abonnement?',
  a: 'Nei. Du betaler én gang og får tilgang til det digitale materiellet. Ingen faste trekk.'
}, {
  q: 'Finnes det en garanti?',
  a: `Ja. ${BRAND} har 7 dager pengene-tilbake-garanti. Hvis materiellet ikke svarer til forventningene dine, kan du kontakte oss og be om å få pengene tilbake innen 7 dager etter kjøpet.`
}];

export const footerLinks = ['Kjøpsvilkår', 'Personvern', 'Refusjon', 'Kontakt'];
